/**
 * Reconciling two different ideas of "which screen is on the toolbar".
 *
 * The two sides of this feature spell a screen differently, and neither is
 * wrong:
 *
 * - The **archive** records `screenPaths` the way the ZIP stored them —
 *   `index.html`, `pages/about.html`, no leading slash, no hash.
 * - The **agent** reports where the iframe actually is, derived from
 *   `location`, as a root-relative path plus whatever hash the prototype put
 *   there: `/pages/about.html#billing`, and `/` for the entry document, which
 *   is served from the bare version URL and therefore has no filename in it at
 *   all.
 *
 * So the dropdown's options and the agent's reports can never be compared with
 * `===`. Everything here exists to make that comparison honest, and it is all
 * pure so it can be tested without a browser.
 */

/** The empty screen path: the version's bare URL, i.e. its entry document. */
const ROOT_PATH = '/';

/**
 * One canonical spelling: leading slash, no `./`, no duplicated separators,
 * hash preserved.
 *
 * The hash is deliberately kept — for a single-page prototype it is the only
 * thing distinguishing one screen from another (spec R3.4).
 */
export const normaliseScreenPath = (raw: string): string => {
  const trimmed = raw.trim();

  if (trimmed === '') {
    return ROOT_PATH;
  }

  const hashIndex = trimmed.indexOf('#');
  const hash = hashIndex === -1 ? '' : trimmed.slice(hashIndex);
  const path = hashIndex === -1 ? trimmed : trimmed.slice(0, hashIndex);

  const cleaned = path
    .replace(/^(?:\.{1,2}\/)+/, '')
    .replace(/\/{2,}/g, '/')
    .replace(/^\/+/, '');

  return `/${cleaned}${hash}`;
};

/** The document part of a screen path, with any hash removed. */
export const screenPathWithoutHash = (raw: string): string => {
  const normalised = normaliseScreenPath(raw);
  const hashIndex = normalised.indexOf('#');

  return hashIndex === -1 ? normalised : normalised.slice(0, hashIndex);
};

/** The hash of a screen path including its `#`, or `''` when it has none. */
export const screenPathHash = (raw: string): string => {
  const hashIndex = raw.indexOf('#');

  return hashIndex === -1 ? '' : raw.slice(hashIndex);
};

/**
 * Which entry of `screenPaths` the iframe is currently showing, or `null`.
 *
 * Returns the *original* string from `screenPaths`, not a normalised one, so
 * the value can be handed straight back to a `<Select>` whose options were
 * built from the same array.
 *
 * Three equivalences are resolved here, in order:
 *
 *  1. **The hash is ignored.** `/settings.html#billing` is the settings screen;
 *     a hash is a position within it, and the dropdown lists documents.
 *  2. **The bare version URL is the entry document.** The agent reports `/` for
 *     it because that is genuinely where the browser is, and only the version's
 *     recorded `entryPath` can say which file that served — so when it is known
 *     it is authoritative, and a nested entry is never mistaken for a
 *     top-level `index.html` the archive happens to also contain.
 *  3. **A directory URL is its `index.html`.** Both for a prototype linking to
 *     `pages/`, and as the fallback for `/` in the moment before the version
 *     detail has loaded — it is the file server's own convention, so it is the
 *     right guess to make in that gap.
 */
export const findMatchingScreenPath = (
  screenPaths: readonly string[],
  current: string | null,
  entryPath: string | null = null,
): string | null => {
  if (current === null) {
    return null;
  }

  const target = screenPathWithoutHash(current);

  const candidates =
    target === ROOT_PATH && entryPath !== null
      ? [screenPathWithoutHash(entryPath)]
      : target.endsWith('/')
        ? [target, `${target}index.html`, `${target}index.htm`]
        : [target];

  return (
    screenPaths.find((path) =>
      candidates.includes(screenPathWithoutHash(path)),
    ) ?? null
  );
};

/**
 * A human label for a screen.
 *
 * The filename alone is what a designer named the screen, and the folder is
 * only worth showing when two screens would otherwise read identically — but
 * the dropdown has no view of its siblings from in here, so the folder is
 * always kept as a dimmed prefix by the component and this returns both parts
 * separately rather than pre-joining them.
 */
export interface ScreenOption {
  /** The original entry from `screenPaths`; the `<Select>`'s value. */
  readonly value: string;
  /** Filename without its extension, title-cased for `index`. */
  readonly label: string;
  /** Containing folder with a trailing slash, or `''` at the root. */
  readonly folder: string;
}

export const toScreenOption = (screenPath: string): ScreenOption => {
  const withoutHash = screenPathWithoutHash(screenPath).replace(/^\//, '');
  const lastSlash = withoutHash.lastIndexOf('/');
  const folder = lastSlash === -1 ? '' : withoutHash.slice(0, lastSlash + 1);
  const file = withoutHash.slice(lastSlash + 1);
  const stem = file.replace(/\.html?$/i, '');

  return {
    value: screenPath,
    label: stem === '' ? file : stem,
    folder,
  };
};

/** Every screen of a version, in the order the archive listed them. */
export const toScreenOptions = (
  screenPaths: readonly string[],
): ScreenOption[] => screenPaths.map(toScreenOption);

/**
 * The path to hand to `atlas:navigate` for a chosen dropdown entry.
 *
 * The protocol asks for "path plus optional hash, relative to the version
 * root", which is the normalised form with its leading slash.
 */
export const toNavigateTarget = (screenPath: string): string =>
  normaliseScreenPath(screenPath);
